import {
  getGetSettlementHistoryApiV1QueryKey,
  useGetPayoutStatusApiV1,
  useGetTaxInformationApiV1,
  useRequestWithdrawalApiV1,
  useResetTestSettlementApiV1,
} from "@/api/__generated__/api.schema.ts";
import { TaxInfoType } from "@/api/__generated__/models";
import { appEnv } from "@/constants/app-env";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePayoutMethods } from "@/routes/payout/hooks/use-payout-methods";
import { useResetPayoutQuery } from "@/routes/payout/hooks/use-reset-payout-query";
import { usePayoutTaxInfo } from "@/routes/payout/hooks/use-tax-info";
import { formatCurrency } from "@/utils/format";
import { Alert, useToastMessage } from "@backpac/monica";
import { Button, Image, Separator, Typography } from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { usePayoutPageTabs } from "../payout-page";
import { SettlementHistoryTable } from "./withdraw/settlement-history-table";

export function PayoutWithdrawContents() {
  const { taxInfo } = usePayoutTaxInfo();
  const {
    data: { payoutMethods },
  } = usePayoutMethods();
  const hasPayoutMethods = payoutMethods.length > 0;

  return (
    <div className="flex flex-col gap-6">
      {!hasPayoutMethods ? <AddYourBank /> : null}
      {appEnv.isStaging ? <ResetPayoutSettlementButton /> : null}
      {hasPayoutMethods && taxInfo == null ? (
        <Alert theme="red">
          To receive payouts, please submit your tax form as required for tax reporting purposes.
        </Alert>
      ) : null}
      <Balance />
      <SettlementHistoryTable />
    </div>
  );
}

function Balance() {
  const workspaceId = useWorkspaceId();
  const { data } = useGetPayoutStatusApiV1(workspaceId);
  const { data: taxInfo } = useGetTaxInformationApiV1(workspaceId);
  const isOnWithdrawal = data?.data.item.latestPayoutHistoryItem?.status === "PENDING";
  const isW9 = taxInfo?.data.item.taxInfo?.type === TaxInfoType["W-9"];
  const taxStatus = taxInfo?.data.item.taxInfo?.status;

  return (
    <div className="p-6 flex flex-col bg-white rounded-[12px] gap-6 max-w-[528px] relative overflow-hidden shadow-standard">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-start">
          <div className="flex gap-[6px] items-center">
            <Typography size="md" color="textPrimary" weight={600}>
              Balance
            </Typography>
          </div>

          <div className="flex items-center gap-1">
            <Typography size="xl" color="textPrimary" weight={600}>
              {formatCurrency(data?.data.item.balance ?? 0)}
            </Typography>
            <Typography size="xs" color="textMuted">
              USD
            </Typography>
          </div>
        </div>

        {isOnWithdrawal ? (
          <Alert theme="orange">
            {formatCurrency(data?.data.item.latestPayoutHistoryItem?.amount ?? 0)} withdrawal in progress
          </Alert>
        ) : null}
        {taxStatus === "PENDING" ? (
          <Alert theme="orange">
            {isW9
              ? "Your tax form is under review and TIN validation is processed every Tuesday. You’ll be notified if there’s any issue."
              : "Your tax form is under review and typically approved within a few business days. You’ll be notified if there’s any issue."}
          </Alert>
        ) : null}
        {taxStatus === "VOIDED" ? (
          <Alert theme="red">Your tax form has been voided. Please submit a new one to receive payouts.</Alert>
        ) : null}
      </div>
      <Separator className="bg-borderLight ml-[-24px] w-[528px]" />
      <div className="flex justify-between items-center gap-6 sm:gap-10">
        <Typography size="paragraphXs" color="textMuted">
          Withdrawals are only available if your balance is at least $20.
        </Typography>
        <WithdrawalButton />
      </div>
    </div>
  );
}

function WithdrawalButton() {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const { error } = useToastMessage();

  const { data, refetch: refetchStatus } = useGetPayoutStatusApiV1(workspaceId);
  const { mutate: requestWithdrawal, isPending } = useRequestWithdrawalApiV1({
    mutation: {
      onSuccess: () => {
        refetchStatus();
        queryClient.invalidateQueries({ queryKey: getGetSettlementHistoryApiV1QueryKey(workspaceId) });
      },
      onError: () => {
        error("Something went wrong");
      },
    },
  });

  const handleWithdraw = () => {
    requestWithdrawal({ workspaceId });
  };

  return (
    <Button size="md" loading={isPending} disabled={!data?.data.item.withdrawalStatus.enabled} onClick={handleWithdraw}>
      Withdraw
    </Button>
  );
}

function AddYourBank() {
  const isMobile = useIsMobile();
  const { setTab } = usePayoutPageTabs();

  return (
    <div className="p-6 flex bg-[#E9F7FF] rounded-[12px] shadow-standard">
      <div className="flex-1">
        <Typography size="md" color="textPrimary" weight={600}>
          Let's get your payouts ready
        </Typography>
        <Typography size="paragraphSm" color="textMuted" font="inter" className="mt-2">
          We’ve refreshed our payout system! Just take a minute to submit (or re-submit) your payout and tax details so
          you can keep getting paid.
        </Typography>
        <Button size="md" className="mt-4" onClick={() => setTab("methods")}>
          Set up now
        </Button>
      </div>
      {!isMobile && <Image src="/assets/payout-bank-right.png" alt="Payout bank" width={200} height={100} />}
    </div>
  );
}

function ResetPayoutSettlementButton() {
  const workspaceId = useWorkspaceId();
  const { success, error } = useToastMessage();
  const resetPayoutQuery = useResetPayoutQuery();
  const { mutate: resetTestSettlement, isPending } = useResetTestSettlementApiV1({
    mutation: {
      onSuccess: () => {
        success("Reset test settlement successfully");
        resetPayoutQuery();
      },
      onError: (_error: Error) => {
        error(_error.message);
      },
    },
  });

  return (
    <div>
      <Button variant="error" size="md" loading={isPending} onClick={() => resetTestSettlement({ workspaceId })}>
        Reset test settlement
      </Button>
    </div>
  );
}
